import { request } from '@remote/api/request'
import {
  guid,
  isMagnetTypeBTLink,
  matchBatchBtLink,
  sendMessage,
  getDeviceId,
  buildFileListTree,
} from '@remote/api/adapter/helper'
import { getErrorMessage } from '@remote/api/adapter/helper/get-download-error-msg'
import type { IErrorRes } from '@remote/type'
import { IRequestInterceptorParams } from '@remote/api/adapter/index.d'
import { safeJSONParse } from '@shared/utils'
import { USER_DOWNLOAD_URL } from '@remote/constant'
import { ITorrentFile, ITreedTorrentNode } from '@remote/api/adapter/type'
import { openBtChoicePop } from '@remote/utils/dialog'
import {
  all as downloadTaskMap,
  useTasks,
  LOCAL_STATUS_DOWNLOAD_TORRENT,
  LOCAL_STATUS_PARSEING,
  LOCAL_STATUS_PARSE_FAIL,
} from '@remote/store/task'
import { logInfo } from '@remote/utils/logger'
import {
  IJobInfo,
  poll2queryJob,
  readFile,
  eraseJob,
} from '@remote/api/adapter/helper/get-job'
import LixianTasks, { events as LixianTasksEvents} from '@remote/api/adapter/helper/batch-create-task'
import LXTaskEventBus, { events } from '@remote/api/adapter/helper/batch-create-task/event-bus'
import { getDownloadIdsMap, getTaskId } from '@remote/api/adapter/helper/batch-create-task/helper'

const createBTTask = async (params: IGetResourceListParams, magnets: string[]): Promise<[IErrorRes, null | string]> => {
  const { userid } = params
  const isBatch = magnets?.length > 1
  const data = {
    user_id: userid,
    [isBatch ? 'urls' : 'url']: isBatch ? magnets.map(item => ({ url: item })) : magnets[0],
    message_id: guid(),
    plat_form: 'pc',
  }
  const method = isBatch ? 'create_lixian_tasks_batch' : 'create_lixian_tasks'
  const [err, res]: [IErrorRes, IGetResourceListRes] = await sendMessage(method, data)

  if (err || !res?.list?.job_id) {
    return [err, null]
  } else {
    return [err, res?.list?.job_id]
  }
}

export const syncInspecBTTask = (url: string, isLimitDownloaded?: boolean): void => {
  const btTreePromise = sendMessage('inspect_lixian_tasks', {
    url: url?.trim(),
    message_id: guid(),
    plat_form: 'pc',
  }).then(async res => {
    const [err, result] = res
    if (!err && result?.list?.inspect_result_path) {
      const btTree = await readFile(result?.list?.inspect_result_path, getDeviceId())
        .then(ftxt => {
          const jsonStr = '[{' + ftxt.replace(/.*\[\{/, '')
          const res = safeJSONParse(jsonStr) as ITorrentFile[]
          return buildFileListTree(Array.isArray(res) ? res : result?.list?.resources || [], true, url, result?.list?.name)
        }).catch(() => {
          return buildFileListTree(result?.list?.resources || [], true, url, result?.list?.name)
        })

      return btTree
    } else {
      throw err?.error_description
    }
  })

  // 处理自动弹出解析面板
  openBtChoicePop({
    isBt: true,
    linkStatus: { ok: 1, invalid: 0, err: 0 },
    btTree: btTreePromise,
    id: '',
    isLimitDownloaded,
  })
}

export const inspecBTTask = async (params: { userid?: string; url: string; originalUrl?: string }, isCancel?: () => boolean): Promise<[IErrorRes, any]> => {
  const { userid = '', url, originalUrl = '' } = params
  return sendMessage('inspect_lixian_tasks', {
    user_id: userid,
    url: url?.trim(),
    message_id: guid(),
    plat_form: 'pc',
  }).then(async res => {
    const [err, result] = res
    if (isCancel && isCancel()) throw '链接已取消'
    
    if (!err && result?.list?.inspect_result_path) {
      const { resources, total_file_nums } = result.list
      let btTree:ITreedTorrentNode[] = []
      if (resources.length >= total_file_nums) {
        btTree = buildFileListTree(result?.list?.resources || [], true, url, result?.list?.name)
      } else {
        btTree = await readFile(result?.list?.inspect_result_path, getDeviceId())
          .then(ftxt => {
            const jsonStr = '[{' + ftxt.replace(/.*\[\{/, '')
            const res = safeJSONParse(jsonStr) as ITorrentFile[]
            return buildFileListTree(Array.isArray(res) ? res : result?.list?.resources || [], true, url, result?.list?.name)
          }).catch(() => {
            return buildFileListTree(result?.list?.resources || [], true, url, result?.list?.name)
          })
      }

      if (isCancel && isCancel()) throw '链接已取消'
      // 处理自动弹出解析面板
      openBtChoicePop({
        isBt: true,
        linkStatus: { ok: 1, invalid: 0, err: 0 },
        btTree: btTree,
        url: originalUrl,
        id: result?.list.download_id,
        isLimitDownloaded: downloadTaskMap[originalUrl] && !['PHASE_TYPE_COMPLETE', 'PHASE_TYPE_LOCAL'].includes(downloadTaskMap[originalUrl]?.phase),
      })
      return [err, { list_id: '', list: { resources: btTree }, isTree: true }]
    } else {
      throw err?.error_description || '链接解析失败'
    }
  })
}

interface IGetResourceListRes {
  list_id: string
  list: {
    page_size: number
    next_page_token: string
    job_id: string
    download_id: string
    name: string
    total_size: string
    total_file_nums: number
    inspect_result_path: string
    resources: null
  }
}

interface IGetResourceListParams {
  userid: string
  page_size: number
  urls: string
  downloadPath?: string
  onlyVideo?: boolean
}

type TReqOptions = IRequestInterceptorParams['options'] & { headers: { Target: string } }

const downloadTorrenFileByBtLink = async (params: IGetResourceListParams, options: TReqOptions, magnets: string[]): Promise<IJobInfo[]> => {
  const { userid } = params
  const reqOptions = Object.assign({
    method: 'POST',
    apiEnv: 'heziDevelopment',
  }, options)
  const [err, job_id] = await createBTTask(params, magnets)
  if (err || !job_id) {
    throw err?.error_description || '链接解析失败'
  } else {
    const biz = 'transport_download'
    return poll2queryJob({
      device_id: options?.headers['Target'],
      userid,
      biz,
      jobIds: [job_id],
      options: {
        onCheck: (jobs) => {
          let errorMsg = ''
          const rejectJob = jobs.find(job => job.status === 5)
          if (rejectJob) errorMsg = getErrorMessage(rejectJob.ext_data)
          return [errorMsg, jobs.every(job => job.status === 1)]
        },
        timeout: 50 * 1000,
        interval: 3 * 1000,
        leading: true,
      },
    }, reqOptions)
  }
}

function handleOnbeforeunload (e) {
  const message = '有下载任务正在进行中，确认将离开当前页面吗？'
  e.preventDefault()
  e.returnValue = message
  return message
}

// 创建单个任务，有文件选择弹窗
const getResourceList = async (params: IGetResourceListParams, options: TReqOptions): Promise<[Partial<IErrorRes>, IGetResourceListRes | null]> => {
  window.addEventListener('beforeunload', handleOnbeforeunload)

  let completedNum = 0
  const taskStore = useTasks()
  const interruptMap = {}
  // 避免下载任务存在id重复
  const aliasMap = {}
  try {
    const { userid, urls } = params
    const [magnets = [], downloadUrls = []] = matchBatchBtLink(urls, true)
    const getDownloadUrl = async (url: string): Promise<string> => {
      let torrentPath: string = url
      if (isMagnetTypeBTLink(url)) {
        const [jobInfo] = await downloadTorrenFileByBtLink(params, options, [url])
        const torrent = safeJSONParse(jobInfo?.ext_data) as { name: string; dir: string }
        const { dir, name } = torrent
        torrentPath = `${dir}/${name}`
        eraseJob({ biz: 'transport_download', device_id: options.headers['Target'], job_id: [jobInfo.job_id] }, options)
      }
      return torrentPath
    }
    const willDownloadUlrs = [...magnets, ...downloadUrls]

    const createTask = async (url: string) => {
      console.log('createTask', url)
      try {
        console.log('待处理中的链接：', url)
        if (interruptMap[url]) {
          return taskStore.update(aliasMap[url] || url, LOCAL_STATUS_PARSE_FAIL, '任务已取消')
        }
        interruptMap[url] = false
        taskStore.update(aliasMap[url] || url, LOCAL_STATUS_DOWNLOAD_TORRENT)
        await getDownloadUrl(url)
          .then(torrentPath => {
            if (interruptMap[url]) {
              return taskStore.update(aliasMap[url] || url, LOCAL_STATUS_PARSE_FAIL, '任务已取消')
            }
            taskStore.update(aliasMap[url] || url, LOCAL_STATUS_PARSEING)
            console.log('待处理中的链接torrent下载完成：', url, torrentPath)
            return inspecBTTask({ userid, url: torrentPath, originalUrl: url }, () => interruptMap[url]).then(() => {
              // update(willDownloadUlrs[i], 0)
              if (interruptMap[url]) {
                return taskStore.update(aliasMap[url] || url, LOCAL_STATUS_PARSE_FAIL, '任务已取消')
              }
              taskStore.pop(aliasMap[url] || url)
              completedNum += 1
              console.log('待处理中的链接解析完成：', url, torrentPath)
              console.log(`%c 已成功解析：${completedNum}个`, 'background:red;color:#FFF')
            })
          })
      } catch (err) {
        if (interruptMap[url]) {
          return taskStore.update(aliasMap[url] || url, LOCAL_STATUS_PARSE_FAIL, '任务已取消')
        }
        interruptMap[url] = true
        taskStore.update(aliasMap[url] || url, LOCAL_STATUS_PARSE_FAIL, err)
        console.log('getResourceList Fail', url, LOCAL_STATUS_PARSE_FAIL, err)
        logInfo('Parse_Url_Fail', { url, err })
      }
    }

    const handleRecordTaskInterrupt = (flag: boolean, taskId: string) => {
      if (taskId === 'all') {
        willDownloadUlrs.forEach(taskId => interruptMap[taskId] = flag)
      } else {
        interruptMap[taskId] = flag
      }
    }

    const handleOnTaskStoreAction = ({ name, args, after }) => {
      if (name === 'update' && args?.length === 4) {
        const [id, , , action] = args
        const taskId = getTaskId(id) || id
        if (action === 'restart' && interruptMap[taskId]) {
          handleRecordTaskInterrupt(false, taskId)
          after(() => {
            createTask(taskId)
          })
        } else if (action === 'pause') {
          handleRecordTaskInterrupt(true, taskId)
        } else if (action === 'delete') {
          handleRecordTaskInterrupt(true, taskId)
        }
      }
    }

    const handleSetTask2Store = (url:string) => {
      const id = taskStore.push(url)
      if (id) aliasMap[url] = id
    }

    LXTaskEventBus.addEventListener(events.update, handleOnTaskStoreAction)
    willDownloadUlrs.forEach(handleSetTask2Store)

    for (let i = 0; i < willDownloadUlrs.length; i++) {
      await createTask(willDownloadUlrs[i])
    }

    window.removeEventListener('beforeunload', handleOnbeforeunload)
    return Promise.resolve([{ error: '', error_description: '', msg: '' }, null])
  } catch (err) {
    const errorRes = {
      error: '链接解析失败',
      error_description: '链接解析失败',
      msg: '链接解析失败',
    }
    window.removeEventListener('beforeunload', handleOnbeforeunload)
    return Promise.resolve([errorRes, null])
  } finally {
    // isEndSub = true
    // if (isCompleted()) taskStore.$dispose()
  }
}
// 全量创建多个任务，无文件选择弹窗
const getBatchResourceList = async (params: IGetResourceListParams, options: TReqOptions): Promise<[Partial<IErrorRes>, IGetResourceListRes | null]> => {
  window.addEventListener('beforeunload', handleOnbeforeunload)

  const taskStore = useTasks()
  const interruptMap = {}

  try {
    const { userid, urls, downloadPath, onlyVideo = false } = params
    const [magnets = [], downloadUrls = []] = matchBatchBtLink(urls, true)

    const willDownloadUlrs = [...downloadUrls, ...magnets]
    const { downloadId2UrlMap, downloadUrl2IdMap } = await getDownloadIdsMap(magnets, downloadUrls)
    // 避免下载任务存在id重复
    const aliasMap = {}
    const lxTask = new LixianTasks({ urls, userid, deviceId: options.headers['Target'], downloadPath, onlyVideo })

    const handleRecordTaskInterrupt = (flag: boolean, url: string) => {
      if (url === 'all') {
        willDownloadUlrs.forEach(url => interruptMap[url] = flag)
      } else {
        interruptMap[url] = flag
      }
    }
    // 监听store中的update action
    const handleOnTaskStoreAction = ({ name, args }) => {
      console.log('handleOnTaskStoreAction', name, args)
      if (name === 'update' && args?.length === 4) {
        const [taskId, , , action] = args
        const id = getTaskId(taskId) || taskId
        const url = downloadId2UrlMap[id]?.url || id
        if (action === 'restart' && interruptMap[url]) {
          handleRecordTaskInterrupt(false, url)
          lxTask.handleRestart(url)
        } else if (action === 'pause') {
          handleRecordTaskInterrupt(true, url)
          lxTask.handlePause(url)
        } else if (action === 'delete') {
          lxTask.handleDelete(url)
        } else if (action === 'recreate') {
          lxTask.handleExistTaskReCreate(url)
        }
      }
    }
    // 监听离线下载类广播更新任务信息
    const handleOnLXTaskUpdate = (id: string, status: number, message = '', isTimeout = false) => {
      console.log('handleOnLXTaskUpdate', id, status, message, isTimeout)
      if (status === 0) {
        taskStore.pop(aliasMap[id] || id)
      } else {
        taskStore.update(aliasMap[id] || id, status, message)
        if (isTimeout) interruptMap[aliasMap[id] || id] = true
      }
    }
    // 当前批量任务处理完成
    const handleOnLXTaskFinish = () => {
      window.removeEventListener('beforeunload', handleOnbeforeunload)
    }

    // 将任务逐个设置到store中
    const handleSetTask2Store = (item: string) => {
      // const downloadId = downloadUrl2IdMap[item] || item
      // const downloadInfo = downloadId2UrlMap[downloadId]
      const localId = taskStore.push(item, -2, item)
      // console.log('handleSetTask2Store', downloadId, localId)
      if (localId) {
        aliasMap[item] = localId
      }
    }

    LXTaskEventBus.addEventListener(events.update, handleOnTaskStoreAction)
    willDownloadUlrs.forEach(handleSetTask2Store)
    lxTask.addEventListener(LixianTasksEvents.update, handleOnLXTaskUpdate)
    lxTask.addEventListener(LixianTasksEvents.finish, handleOnLXTaskFinish)
    lxTask.init()
    return Promise.resolve([{ error: '', error_description: '', msg: '' }, null])
  } catch (err) {
    const errorRes = {
      error: '链接解析失败',
      error_description: '链接解析失败',
      msg: '链接解析失败',
    }
    window.removeEventListener('beforeunload', handleOnbeforeunload)
    return Promise.resolve([errorRes, null])
  }
}

// 拦截创建下载任务
export function interceptGetResourceList ({ config, response }: { config: IRequestInterceptorParams; response: unknown }) {
  const getResourceListApiUrl = 'api-hezi.xunlei.com/drive/v1/resource/list'
  if (config.url.includes(getResourceListApiUrl)) {
    // 在此处做差异化，区分单条任务和多条任务
    const { urls } = config.params
    const [magnets = [], downloadUrls = []] = matchBatchBtLink(urls, true)
    const isBatch = magnets.length > 1 || downloadUrls.length > 0
    return { config, response: isBatch ? getBatchResourceList(config.params as IGetResourceListParams, config.options as TReqOptions) : getResourceList(config.params as IGetResourceListParams, config.options as TReqOptions) }
  }
  return { config, response }
}

interface ICreateTaskParams {
  user_id: string
  url: string
  size: string
  index
  space: string
  type: 'user#download-url'
  file_name: string
  file_size: string
  params: {
    target: string
    platform: string
    url: string
    total_file_count: string
    parent_folder_id: string
  }
  indexs: { index: number; size: number }
  downloadPath?: string
}

// 拦截创建下载任务
export function interceptCreateTask ({ config, response }: { config: IRequestInterceptorParams<ICreateTaskParams>; response: unknown }) {
  const getResourceListApiUrl = 'api-hezi.xunlei.com/drive/v1/task'
  if (
    config.url.endsWith(getResourceListApiUrl) &&
    config.params.type === USER_DOWNLOAD_URL &&
    config.options.method === 'POST'
  ) {
    const { user_id: userid, file_name, file_size: size, indexs } = config.params
    console.log('interceptCreateTask indexs', indexs)
    // const indexs = [{ file_size: +size, index }]
    const reqOptions = Object.assign({
      method: 'POST',
      apiEnv: 'heziDevelopment',
    }, config.options)

    const reqParams = {
      type: 'call',
      call_id: guid(),
      data: {
        method: 'create_lixian_tasks',
        param: {
          user_id: userid,
          url: config?.params?.params?.url,
          indexs,
          message_id: guid(),
          plat_form: 'pc',
          page: 1,
          page_size: 50,
          file_size: size,
          file_name,
          download_path: config?.params?.downloadPath || '',
          recreate: true,
        },
      },
    }
    return { config, response: request('/drive/v1/task', reqParams, reqOptions) }
  }
  return { config, response }
}

interface IActionTaskParams {
  user_id: string
  space: string
  type: string
  id: string
  set_params: {
    spec: string
  }
}
// 拦截下载任务操作
export function interceptActionTask ({ config, response }: { config: IRequestInterceptorParams<IActionTaskParams>; response: unknown }) {
  const getResourceListApiUrl = 'api-hezi.xunlei.com/drive/v1/task'
  if (
    config.url.endsWith(getResourceListApiUrl) &&
    config.params.type === USER_DOWNLOAD_URL &&
    config.options.method === 'PATCH'
  ) {
    const { user_id: userid, set_params, id } = config.params
    const reqOptions = Object.assign({
      method: 'POST',
      apiEnv: 'heziDevelopment',
    }, config.options)
    const spec = safeJSONParse(set_params.spec) as { phase: string }

    const method = spec?.phase === 'pause' ? 'pause_lixian_tasks' : 'resume_lixian_tasks'
    const reqParams = {
      type: 'call',
      call_id: guid(),
      data: {
        method: method,
        param: {
          user_id: userid,
          message_id: guid(),
          plat_form: 'pc',
          download_ids: [id],
        },
      },
    }
    return { config, response: request('/drive/v1/task', reqParams, reqOptions) }
  }
  return { config, response }
}